"use client";

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { LogOut, FileText, User, RefreshCw } from "lucide-react";

import BackToMenu from "./BackToMenu.jsx";
import { useAuth } from "../context/AuthContext";
import { apiConfig } from "../config/api.config";
import AlmacenCertificadoModal from "./AlmacenCertificadoModal.jsx";

import logoAlcaldia from "../assets/logocopacabana.png";

// Roles
const ROLE_MAP = {
  1: "solicitante",
  2: "planeacion",
  3: "hacienda",
  4: "juridica",
  5: "alcalde",
  6: "administrador",
  7: "presupuesto",
  8: "almacen",
  9: "validador",
  10: "firma_secretario",
  11: "firma_alcalde",
};

const ROLE_LABELS = {
  solicitante: "Solicitante",
  planeacion: "Planeación",
  hacienda: "Hacienda",
  juridica: "Jurídica",
  alcalde: "Alcalde",
  administrador: "Administrador",
  presupuesto: "Presupuesto",
  almacen: "Almacén",
  validador: "Validador (Inexistencia)",
  firma_secretario: "Secretario (Inexistencia)",
  firma_alcalde: "Alcalde (Inexistencia)",
};

// Util: formateo fecha (compatible)
function formatDateShort(val) {
  if (!val) return "Sin fecha";

  // Si viene como Date
  if (val instanceof Date && !Number.isNaN(val.getTime())) {
    return val.toLocaleDateString("es-CO");
  }

  const s = String(val).trim();

  // YYYY-MM-DD o YYYY-MM-DDT00:00:00...
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    return new Date(y, mo, d).toLocaleDateString("es-CO");
  }

  const dt = new Date(s);
  if (!Number.isNaN(dt.getTime())) return dt.toLocaleDateString("es-CO", { timeZone: "America/Bogota" });

  return s;
}

// Util: etiqueta fase (alineada con Dashboard principal)
function phaseLabel(phase) {
  if (phase === null || phase === undefined || String(phase).trim() === "") return "—";
  const p = Number(phase);
  if (!Number.isFinite(p)) return "—";

  const map = {
    1: "Planeación",
    2: "Hacienda",
    3: "Jurídica",
    4: "Alcalde",
    5: "Aprobado por Alcalde",
  };
  return map[p] || `Fase ${p}`;
}

// Badge estado estilo Dashboard
function normalizeStatus(status) {
  return String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function getStatusBadgeStyle(status) {
  const s = normalizeStatus(status);
  switch (s) {
    case "en_proceso":
    case "en_tramite":
    case "en_trámite":
      return { backgroundColor: "#fef9c3", color: "#854d0e" };
    case "aprobado":
      return { backgroundColor: "#dcfce7", color: "#166534" };
    case "rechazado":
      return { backgroundColor: "#fee2e2", color: "#b91c1c" };
    default:
      return { backgroundColor: "#f3f4f6", color: "#374151" };
  }
}

function getStatusLabel(status) {
  const s = normalizeStatus(status);
  switch (s) {
    case "en_proceso":
    case "en_tramite":
    case "en_trámite":
      return "En proceso";
    case "aprobado":
      return "Aprobado";
    case "rechazado":
      return "Rechazado";
    default:
      return status || "Desconocido";
  }
}

export default function AlmacenSolicitudes() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // contratos normalizados para tabla
  const [rows, setRows] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  const { user, userRole: rawUserRole, logout } = useAuth();

  const role =
    typeof rawUserRole === "number"
      ? ROLE_MAP[rawUserRole] || ""
      : String(rawUserRole || "").trim().toLowerCase();

  const roleLabel = ROLE_LABELS[role] || rawUserRole || "Sin rol";

  const isMobile = windowSize.width < 768;

  useEffect(() => {
    function handleResize() {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /**
   * ✅ Listado sale de /api/contracts/index.php
   * ✅ Modal carga solicitud por /api/almacen/solicitudes?contract_id=...
   */
  const fetchContratos = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const contractsEndpoint = apiConfig.endpoints.contracts || "/api/contracts/index.php";
      const contractsUrl = apiConfig.getUrl(contractsEndpoint);

      const resp = await fetch(contractsUrl, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      if (!resp.ok) {
        const txt = await resp.text().catch(() => "");
        throw new Error(
          txt?.trim()
            ? txt
            : resp.status === 401
            ? "No se encontró token de autenticación"
            : `Error HTTP ${resp.status}`
        );
      }

      const payload = await resp.json().catch(() => null);

      const contractsArr = Array.isArray(payload?.contracts)
        ? payload.contracts
        : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];

      const built = contractsArr.map((c) => {
        const contractId = String(c?.id || c?.contract_id || c?.contractId || "").trim();

        const fecha = c?.fecha ?? c?.created_at ?? c?.updated_at ?? c?.fecha_solicitud ?? null;

        const secretaria =
          c?.secretaria ?? c?.dependencia ?? c?.dependencia_contratante ?? "No especificada";

        const radicado =
          c?.radicado ?? c?.certificado_numero ?? c?.certificadoNumero ?? c?.numero_radicado ?? "Sin radicado";

        const nombre =
          c?.nombre_contratista ??
          c?.nombre_contratante ??
          c?.contratista_nombre ??
          c?.contratista ??
          "Por definir";

        const estado = c?.estado ?? c?.status ?? c?.estado_contrato ?? "—";

        const phase =
          c?.fase ?? c?.current_phase ?? c?.phase ?? c?.current_phase_id ?? c?.fase_actual ?? null;

        return {
          key: contractId || Math.random().toString(16).slice(2),
          contract_id: contractId,
          fecha,
          secretaria,
          radicado,
          nombre,
          estado,
          phase,
          _contract: c,
        };
      });

      setRows(built);
    } catch (e) {
      setRows([]);
      setError(e?.message || "Error cargando contratos para almacén");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContratos();
  }, [fetchContratos]);

  const handleVerSolicitud = async (row) => {
    try {
      const contractId = String(row?.contract_id || row?._contract?.id || "").trim();
      if (!contractId) return;

      const contract = row?._contract && typeof row._contract === "object" ? row._contract : {};

      const almacenEndpoint =
        apiConfig.endpoints.almacenSolicitudes || "/api/almacen/solicitudes/index.php";

      const reqUrl = apiConfig.getUrl(almacenEndpoint, `contract_id=${encodeURIComponent(contractId)}`);

      const reqResp = await fetch(reqUrl, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      if (!reqResp.ok) {
        const txt = await reqResp.text().catch(() => "");
        throw new Error(txt?.trim() ? txt : `Error cargando solicitud almacén: ${reqResp.status}`);
      }

      const reqPayload = await reqResp.json().catch(() => null);

      const reqArr = Array.isArray(reqPayload)
        ? reqPayload
        : Array.isArray(reqPayload?.data)
        ? reqPayload.data
        : Array.isArray(reqPayload?.solicitudes)
        ? reqPayload.solicitudes
        : [];

      const req = reqArr?.[0] || null;

      const requestIdRaw = req?.request_id ?? req?.id ?? null;
      const requestId = requestIdRaw ? String(requestIdRaw).replace(/[{}]/g, "").trim() : null;

      const contractForModal = {
  ...contract,

  contract_id: contractId,
  almacen_request_id: requestId,
  request_id: requestId,

  unspsc: req?.unspsc ?? contract?.unspsc ?? "",
  descripcion: req?.descripcion ?? contract?.descripcion ?? "",
  correo: req?.correo ?? contract?.correo ?? "",
  contacto: req?.contacto ?? contract?.contacto ?? "",

  // ✅ Pasar los valores de rubros del contrato original
  valor: contract?.valor ?? null,
  valores: contract?.valores ?? null,
  presupuesto_total: contract?.presupuesto_total ?? contract?.presupuestoTotal ?? null,
  valor_solicitado: contract?.valor_solicitado ?? contract?.presupuesto_total ?? null,

  form_data: req?.form_data ?? null,
  form_data_obj: req?.form_data_obj ?? null,

  _almacen_request: req,
};

      setSelectedContract(contractForModal);
      setShowModal(true);
    } catch (e) {
      alert(e?.message || "No se pudo abrir la solicitud");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedContract(null);
    fetchContratos();
  };

  const hasRows = useMemo(() => Array.isArray(rows) && rows.length > 0, [rows]);

  // Column widths igual patrón Dashboard
  const getColumnWidths = () => {
    if (isMobile) {
      return {
        fecha: "10%",
        secretaria: "12%",
        radicado: "10%",
        contratista: "16%",
        estado: "10%",
        fase: "16%",
        acciones: "12%",
        solicitudes: "14%",
      };
    }
    return {
      fecha: "10%",
      secretaria: "14%",
      radicado: "10%",
      contratista: "18%",
      estado: "10%",
      fase: "16%",
      acciones: "11%",
      solicitudes: "11%",
    };
  };

  const columnWidths = getColumnWidths();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "linear-gradient(to bottom, #1e3a8a, #1e40af)",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: "1",
          padding: isMobile ? "1rem" : "2rem",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            maxWidth: "1200px",
            width: "100%",
            backgroundColor: "white",
            borderRadius: isMobile ? "0.5rem" : "1rem",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
          }}
        >
          {/* Barra superior */}
          <div
            style={{
              padding: isMobile ? "0.75rem" : "1rem",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "flex-start",
              backgroundColor: "#f9fafb",
            }}
          >
            <BackToMenu label="Volver al menú" />
          </div>

          {/* Header */}
          <div
            style={{
              padding: isMobile ? "1rem" : "1.5rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <img
              src={logoAlcaldia || "/placeholder.svg"}
              alt="Logo Alcaldía"
              style={{
                height: isMobile ? "120px" : "200px",
                width: "auto",
                maxWidth: "100%",
              }}
            />

            <h2
              style={{
                fontSize: isMobile ? "1.3rem" : "1.5rem",
                fontWeight: "bold",
                marginBottom: "0.5rem",
                color: "#1e3a8a",
                textAlign: "center",
              }}
            >
              Contratos
            </h2>

            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "0.75rem",
                width: "100%",
                gap: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#eff6ff",
                  padding: "0.5rem 1rem",
                  borderRadius: "9999px",
                }}
              >
                <User size={16} style={{ marginRight: "0.5rem", color: "#1e40af" }} />
                <span style={{ fontSize: "0.875rem", color: "#1e40af" }}>
                  {user?.email} ({roleLabel})
                </span>
              </div>

              <button
                onClick={logout}
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#ef4444",
                  color: "white",
                  padding: "0.5rem 1rem",
                  borderRadius: "9999px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                <LogOut size={16} style={{ marginRight: "0.5rem" }} />
                Cerrar sesión
              </button>
            </div>
          </div>

          {/* Contenido / Tabla */}
          <div style={{ padding: isMobile ? "0.75rem" : "1.5rem", width: "100%", boxSizing: "border-box" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.75rem" }}>
              <button
                type="button"
                onClick={fetchContratos}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  backgroundColor: "#1e40af",
                  color: "white",
                  padding: "0.5rem 0.9rem",
                  borderRadius: "9999px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: "700",
                }}
              >
                <RefreshCw size={16} />
                Actualizar
              </button>
            </div>

            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "3rem",
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderRadius: "0.5rem",
                  fontWeight: 700,
                }}
              >
                Cargando...
              </div>
            ) : error ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "3rem",
                  color: "#ef4444",
                  backgroundColor: "#f9fafb",
                  borderRadius: "0.5rem",
                  fontWeight: 800,
                }}
              >
                {error}
              </div>
            ) : !hasRows ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "3rem",
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderRadius: "0.5rem",
                }}
              >
                <FileText size={48} style={{ margin: "0 auto 1rem", color: "#9ca3af" }} />
                <p>No hay solicitudes disponibles</p>
              </div>
            ) : (
              <div style={{ overflowX: isMobile ? "auto" : "visible" }}>
                <table
                  style={{
                    width: "100%",
                    tableLayout: isMobile ? "auto" : "fixed",
                    borderCollapse: "separate",
                    borderSpacing: "0",
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                    fontSize: isMobile ? "0.8rem" : "1rem",
                  }}
                >
                  <colgroup>
                    <col style={{ width: columnWidths.fecha }} />
                    <col style={{ width: columnWidths.secretaria }} />
                    <col style={{ width: columnWidths.radicado }} />
                    <col style={{ width: columnWidths.contratista }} />
                    <col style={{ width: columnWidths.estado }} />
                    <col style={{ width: columnWidths.fase }} />
                    <col style={{ width: columnWidths.acciones }} />
                    <col style={{ width: columnWidths.solicitudes }} />
                  </colgroup>

                  <thead>
                    <tr style={{ backgroundColor: "#1e40af", color: "white" }}>
                      {["Fecha", "Secretaría", "Radicado", "Nombre del Contratista", "Estado", "Fase", "Acciones", "Solicitudes"].map(
                        (h) => (
                          <th
                            key={h}
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              textAlign: "left",
                              fontSize: isMobile ? "0.7rem" : "0.75rem",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={r.key} style={{ backgroundColor: idx % 2 === 0 ? "white" : "#f9fafb" }}>
                        <td
                          style={{
                            padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                            fontSize: isMobile ? "0.75rem" : "0.875rem",
                            borderBottom: "1px solid #e5e7eb",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatDateShort(r.fecha)}
                        </td>

                        <td
                          style={{
                            padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                            fontSize: isMobile ? "0.75rem" : "0.875rem",
                            borderBottom: "1px solid #e5e7eb",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: isMobile ? "normal" : "nowrap",
                          }}
                        >
                          {r.secretaria || "No especificada"}
                        </td>

                        <td
                          style={{
                            padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                            fontSize: isMobile ? "0.75rem" : "0.875rem",
                            borderBottom: "1px solid #e5e7eb",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.radicado || "Sin radicado"}
                        </td>

                        <td
                          style={{
                            padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                            fontSize: isMobile ? "0.75rem" : "0.875rem",
                            borderBottom: "1px solid #e5e7eb",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={r.nombre || "Por definir"}
                        >
                          {r.nombre || "Por definir"}
                        </td>

                        <td
                          style={{
                            padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                            fontSize: isMobile ? "0.75rem" : "0.875rem",
                            borderBottom: "1px solid #e5e7eb",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              padding: "0.25rem 0.75rem",
                              borderRadius: "9999px",
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              ...getStatusBadgeStyle(r.estado),
                            }}
                          >
                            {getStatusLabel(r.estado)}
                          </span>
                        </td>

                        <td
                          style={{
                            padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                            fontSize: isMobile ? "0.75rem" : "0.875rem",
                            borderBottom: "1px solid #e5e7eb",
                            whiteSpace: "normal",
                            lineHeight: "1.3",
                          }}
                        >
                          {phaseLabel(r.phase)}
                        </td>

                        <td
                          style={{
                            padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                            fontSize: isMobile ? "0.75rem" : "0.875rem",
                            borderBottom: "1px solid #e5e7eb",
                            textAlign: "center",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleVerSolicitud(r)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#1e40af",
                              color: "white",
                              padding: isMobile ? "0.4rem 0.5rem" : "0.5rem 0.75rem",
                              borderRadius: "0.375rem",
                              border: "none",
                              fontSize: isMobile ? "0.7rem" : "0.75rem",
                              fontWeight: "700",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              gap: "0.4rem",
                            }}
                          >
                            <FileText size={14} />
                            Ver detalles
                          </button>
                        </td>

                        <td
                          style={{
                            padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                            fontSize: isMobile ? "0.75rem" : "0.875rem",
                            borderBottom: "1px solid #e5e7eb",
                            textAlign: "center",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleVerSolicitud(r)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#6b7280",
                              color: "white",
                              padding: isMobile ? "0.4rem 0.5rem" : "0.5rem 0.75rem",
                              borderRadius: "0.375rem",
                              border: "none",
                              fontSize: isMobile ? "0.7rem" : "0.75rem",
                              fontWeight: "700",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Ver solicitudes
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && selectedContract && (
        <AlmacenCertificadoModal
          show={showModal}
          onClose={handleModalClose}
          contrato={selectedContract}
          userRole={role}
        />
      )}
    </div>
  );
}
